import { useEffect, useState } from "react";
import { Icon } from "../ui";
import type { Tweaks as TweaksState } from "../types";

export interface TweaksProps {
  tweaks: TweaksState;
  onChange: (next: TweaksState) => void;
}

export function Tweaks({ tweaks, onChange }: TweaksProps) {
  const [open, setOpen] = useState(false);

  // Alt+T toggles the panel — replaces the iframe postMessage protocol from
  // the design bundle (which only worked inside the Claude Design preview).
  useEffect(() => {
    const onKey = (ev: KeyboardEvent) => {
      if (ev.altKey && (ev.key === "t" || ev.key === "T")) {
        ev.preventDefault();
        setOpen((o) => !o);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const set = <K extends keyof TweaksState>(k: K, v: TweaksState[K]) => {
    onChange({ ...tweaks, [k]: v });
  };

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        title="Abrir Tweaks (Alt+T)"
        className="fixed bottom-4 right-4 z-40 hidden w-9 h-9 border border-[var(--ink)] bg-[var(--paper)] text-[var(--ink)] hover:bg-[var(--ink)] hover:text-[var(--paper)] transition-colors md:flex items-center justify-center"
      >
        <Icon.Settings size={15} strokeWidth={1.75} />
      </button>
    );
  }

  return (
    <div
      className="fixed bottom-6 right-6 z-50 hidden w-80 bg-[var(--paper)] border-2 border-[var(--ink)] anim-rise md:block"
      style={{ boxShadow: "0 20px 40px -20px rgba(0,0,0,0.25)" }}
    >
      <div className="flex items-center justify-between px-5 py-3 border-b-2 border-[var(--ink)] drag-handle">
        <div>
          <div className="eyebrow">§ Ajustes</div>
          <div className="display-5">Tweaks</div>
        </div>
        <button
          type="button"
          onClick={() => setOpen(false)}
          className="text-[var(--ink-3)] hover:text-[var(--ink)]"
        >
          <Icon.X size={18} />
        </button>
      </div>

      <div className="px-5 py-5 space-y-5">
        <div>
          <div className="label mb-2">Modo</div>
          <div className="grid grid-cols-2 gap-0 border border-[var(--ink)]">
            <button
              type="button"
              onClick={() => set("mock", true)}
              className={"py-2 text-[12px] uppercase tracking-[0.06em] transition-colors " + (tweaks.mock ? "bg-[var(--ink)] text-[var(--paper)]" : "bg-[var(--paper)] text-[var(--ink)] hover:bg-[var(--paper-2)]")}
            >
              Com mock
            </button>
            <button
              type="button"
              onClick={() => set("mock", false)}
              className={"py-2 text-[12px] uppercase tracking-[0.06em] transition-colors border-l border-[var(--ink)] " + (!tweaks.mock ? "bg-[var(--ink)] text-[var(--paper)]" : "bg-[var(--paper)] text-[var(--ink)] hover:bg-[var(--paper-2)]")}
            >
              Vazio
            </button>
          </div>
          <div className="caption mt-1.5">
            {tweaks.mock ? "Sertralina pré-carregada" : "Estados vazios para testar zero-state"}
          </div>
        </div>

        <div>
          <div className="label mb-2">Densidade</div>
          <div className="grid grid-cols-2 gap-0 border border-[var(--ink)]">
            <button
              type="button"
              onClick={() => set("density", "compact")}
              className={"py-2 text-[12px] uppercase tracking-[0.06em] transition-colors " + (tweaks.density === "compact" ? "bg-[var(--ink)] text-[var(--paper)]" : "bg-[var(--paper)] text-[var(--ink)] hover:bg-[var(--paper-2)]")}
            >
              Compacta
            </button>
            <button
              type="button"
              onClick={() => set("density", "comfortable")}
              className={"py-2 text-[12px] uppercase tracking-[0.06em] transition-colors border-l border-[var(--ink)] " + (tweaks.density === "comfortable" ? "bg-[var(--ink)] text-[var(--paper)]" : "bg-[var(--paper)] text-[var(--ink)] hover:bg-[var(--paper-2)]")}
            >
              Confortável
            </button>
          </div>
        </div>

        <div>
          <div className="flex items-baseline justify-between mb-2">
            <span className="label">Escala tipográfica</span>
            <span className="mono text-[11px] text-[var(--ink)] tabular">
              ×{tweaks.displayScale.toFixed(2)}
            </span>
          </div>
          <input
            type="range"
            min="0.85"
            max="1.15"
            step="0.05"
            value={tweaks.displayScale}
            onChange={(e) => set("displayScale", parseFloat(e.target.value))}
            className="w-full accent-[var(--ink)]"
          />
          <div className="caption mt-1">Escala global aplicada em headings e display</div>
        </div>

        <div className="flex items-center justify-between pt-3 border-t border-[var(--rule)]">
          <span className="label">Grade</span>
          <button
            type="button"
            onClick={() => set("showGrid", !tweaks.showGrid)}
            className={"relative w-10 h-5 transition-colors " + (tweaks.showGrid ? "bg-[var(--ink)]" : "bg-[var(--rule)]")}
          >
            <span
              className={"absolute top-0.5 left-0.5 w-4 h-4 bg-[var(--paper)] transition-transform " + (tweaks.showGrid ? "translate-x-5" : "")}
            />
          </button>
        </div>
      </div>

      <div className="px-5 py-3 border-t border-[var(--rule)] caption flex items-center justify-between">
        <span>Persistente · Alt+T</span>
        <span className="mono">v0.1</span>
      </div>
    </div>
  );
}
